<template>
  <v-form :ref="`${group}Form`">
    <v-row class="sections">
      <v-col
        v-for="section in sections"
        :key="section.title"
        :cols="section.width"
        class="px-2"
      >
        <v-card outlined class="flex-col-start-start gap-3 pt-4 px-6">
          <div class="text-h6">{{ section.title }}</div>
          <v-row class="w-100">
            <v-col
              v-for="field in section.fields"
              :key="field.key"
              :cols="
                section.fields.length === 1
                  ? 12
                  : section.fields.length === 2
                  ? 6
                  : 3
              "
            >
              <div class="text-subtitle-1 mb-2 en-font">{{ field.title }}</div>
              <v-text-field
                background-color="background"
                type="number"
                :readonly="field.readonly"
                :disabled="field.readonly"
                :rules="[
                  rules.required,
                  ...(field.readonly
                    ? []
                    : [
                        rules.range(groupData[field.key], field.min, field.max),
                      ]),
                ]"
                :value="groupData[field.key]"
                @input="(val) => handleValueChange(val, field.key)"
                @blur="
                  proceededBySection[group] || proceededBySection[section.title]
                    ? normalizeSection(section)
                    : checkTotalPercents(section)
                "
                solo
                flat
                dense
              />
            </v-col>
          </v-row>
        </v-card>
        <v-dialog
          v-if="sectionError && sectionError === section.title"
          v-model="confirmDialog"
          max-width="500"
        >
          <v-card>
            <v-card-title class="text-h6">{{
              $t("percent_not_normalized_title")
            }}</v-card-title>
            <v-card-text style="direction: rtl">
              {{ $t("percent_not_normalized_body") }}
            </v-card-text>
            <v-card-actions class="justify-end">
              <v-btn text @click="onCancelProceed">{{ $t("cancel") }}</v-btn>
              <v-btn
                color="primary"
                class="white--text"
                @click="onProceedAnyway"
              >
                {{ $t("proceed") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
      <v-col cols="8" v-if="enableActions">
        <v-btn
          v-if="enableSubmit"
          class="white--text"
          color="infoGreen"
          min-width="250"
          :loading="submitLoading"
          @click="submit"
        >
          {{ $t("calculateAndSubmit") }}
        </v-btn>
        <v-btn
          v-else
          class="white--text"
          color="mainBlue"
          min-width="150"
          @click="next"
        >
          {{ $t("next") }}
        </v-btn>
      </v-col>
      <v-col
        v-if="!disablePrev && enableActions"
        cols="4"
        class="flex-row-end-center"
      >
        <v-btn
          class="black--text"
          color="lightGrey"
          min-width="150"
          @click="back"
        >
          {{ $t("previous") }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { VALIDATION_RULES } from "@/core/constants";

export default {
  name: "BalanceSections",

  props: {
    disablePrev: { type: Boolean, default: false },
    enableSubmit: { type: Boolean, default: false },
    enableActions: { type: Boolean, default: true },
    submitLoading: { type: Boolean, default: false },
    group: { type: String },
    sections: { type: Array },
    groupData: { type: Object },
  },

  data() {
    return {
      rules: VALIDATION_RULES,
      sectionError: null,
      confirmDialog: false,
      proceededBySection: {},
      nextIsBlocked: false,
    };
  },

  methods: {
    onProceedAnyway() {
      if (this.sectionError) {
        this.proceededBySection = {
          ...this.proceededBySection,
          [this.group]: true,
          [this.sectionError]: true,
        };
        this.sections
          .filter((s) => s.checkSetionTotal)
          .forEach((s) => this.normalizeSection(s));
      }
      this.confirmDialog = false;
      this.nextIsBlocked = false;
      this.$emit("disableError");
      this.sectionError = null;
    },
    onCancelProceed() {
      this.confirmDialog = false;
    },
    validate() {
      const validationResult = this.$refs[`${this.group}Form`].validate();
      return validationResult;
    },
    next() {
      this.nextIsBlocked = false;
      this.sections.some((sec) => {
        this.checkTotalPercents(sec);
        return this.nextIsBlocked;
      });
      if (this.nextIsBlocked) {
        this.confirmDialog = true;
        this.$emit("enableError");
        return;
      }
      const valid = this.validate();
      if (valid) this.$emit("next");
      else this.$emit("enableError");
    },
    back() {
      this.$emit("back");
    },
    submit() {
      this.nextIsBlocked = false;
      this.sections.some((sec) => {
        this.checkTotalPercents(sec);
        return this.nextIsBlocked;
      });
      if (this.nextIsBlocked) {
        this.confirmDialog = true;
        this.$emit("enableError");
        return;
      }
      const valid = this.validate();
      if (valid) {
        this.proceededBySection = {};
        this.sectionError = null;
        this.nextIsBlocked = false;
        this.confirmDialog = false;
        this.$emit("submit");
      } else {
        this.$emit("enableError");
      }
    },
    handleValueChange(value, key) {
      this.$emit("valueChange", { key, value });
    },
    checkTotalPercents(section) {
      if (section.checkSetionTotal) {
        const sectionFields = section.fields.map((field) => {
          if (field.title.includes("%")) return field.key;
        });
        const sectionData = Object.entries(this.groupData)
          .map(([key, value]) => {
            if (sectionFields.includes(key)) return value;
          })
          .filter((item) => item !== undefined);
        const total = sectionData.reduce((sum, val) => (sum += val), 0);
        if (Number(total.toFixed(0)) !== 100) {
          if (
            this.proceededBySection[this.group] ||
            this.proceededBySection[section.title]
          ) {
            this.nextIsBlocked = false;
            this.$emit("disableError");
            return;
          }
          this.sectionError = section.title;
          this.nextIsBlocked = true;
          this.$emit("enableError");
        } else {
          this.nextIsBlocked = false;
          this.$emit("disableError");
        }
      }
    },
    normalizeSection(section) {
      if (!section?.checkSetionTotal) return;
      const percentKeys = section.fields
        .filter((f) => typeof f?.title === "string" && f.title.includes("%"))
        .map((f) => f.key);
      if (!percentKeys.length) return;
      const values = percentKeys.map((k) => Number(this.groupData[k] ?? 0));
      const sum = values.reduce((acc, v) => acc + (isNaN(v) ? 0 : v), 0);
      if (!sum) return;
      const scale = 100 / sum;
      const rounded = values.map((v) => Number((v * scale).toFixed(3)));
      percentKeys.forEach((key, idx) => {
        this.$emit("valueChange", { key, value: rounded[idx] });
      });
      this.nextIsBlocked = false;
      this.$emit("disableError");
    },
  },
};
</script>
